import React from "react";
import { useIsMobileShell } from "../../common/navLayout";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { TroubleshootingTab } from "./troubleshootingInterfaces";
import { useIsStaffUser } from "../../utils/staffUser";

const theme = createTheme({ palette: { primary: { main: "#FF8400" } } });

function a11yProps(index: number) {
  return {
    id: `troubleshooting-tab-${index}`,
    "aria-controls": `troubleshooting-tabpanel-${index}`,
  };
}

const tabSx = { fontFamily: "Mulish", fontWeight: "Bold", color: "primary" };

export const TROUBLESHOOTING_TABS: {
  value: TroubleshootingTab;
  label: string;
  /** Shown to Actuate staff only (internal_user, else super_user). */
  staffOnly?: boolean;
}[] = [
  { value: "diagnosis", label: "Diagnosis" },
  { value: "connections", label: "Connections" },
  { value: "wireguard", label: "WireGuard" },
  { value: "smtp-cameras", label: "SMTP Cameras" },
  { value: "alarm-receivers", label: "Alarm Receivers" },
  { value: "fisheye-dewarp", label: "Fisheye dewarp", staffOnly: true },
];

/** The tabs this user sees. */
export const visibleTroubleshootingTabs = (staff: boolean) =>
  TROUBLESHOOTING_TABS.filter((tab) => staff || !tab.staffOnly);

/**
 * The tab to show: a staff-only tab remembered from a staff session (or a
 * shared machine) falls back to Diagnosis for everyone else.
 */
export const visibleTroubleshootingTab = (
  tab: TroubleshootingTab,
  staff: boolean
): TroubleshootingTab =>
  visibleTroubleshootingTabs(staff).some((row) => row.value === tab)
    ? tab
    : "diagnosis";

/** Same shape as WireGuardPageTabs so the two pages read as one family. */
export const TroubleshootingPageTabs = ({
  activeTab,
  setActiveTab,
  right,
}: {
  activeTab: TroubleshootingTab;
  setActiveTab: (tab: TroubleshootingTab) => void;
  /** Slot on the right of the tab strip: last-checked time and Run all checks. */
  right?: React.ReactNode;
}) => {
  const mobile = useIsMobileShell();
  const staff = useIsStaffUser();
  const tabs = visibleTroubleshootingTabs(staff);
  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        backgroundColor: "white",
        display: "flex",
        alignItems: "center",
        pr: 2,
      }}
    >
      <ThemeProvider theme={theme}>
        <Tabs
          value={visibleTroubleshootingTab(activeTab, staff)}
          onChange={(_, value: TroubleshootingTab) => setActiveTab(value)}
          aria-label="troubleshooting_page_tabs"
          color="primary"
          variant="scrollable"
          // Phones: always show the arrows (disabled at either end). "auto"
          // relies on an IntersectionObserver that did not fire in a 390px
          // check, leaving nothing to say more tabs are off to the side.
          scrollButtons={mobile ? true : "auto"}
          allowScrollButtonsMobile
          // MUI hides a disabled arrow (opacity 0); keep it faintly visible
          // so phones still see there is a tab row to scroll (QA MB-01).
          sx={{
            flex: 1,
            minWidth: 0,
            "& .MuiTabScrollButton-root.Mui-disabled": { opacity: 0.3 },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={tab.label}
              sx={tabSx}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </ThemeProvider>
      {right}
    </Box>
  );
};
